import { useTrans } from "@/translations";

import { useContext, useMemo } from "react";

import { RecyclingRateTrendContext } from "../context";
import type { RecyclingRatePoint } from "../types";

type Props = {
  active?: boolean;
  payload?: { payload?: RecyclingRatePoint }[];
  label?: string;
};

type TooltipData = {
  key: string;
  name: string;
  color: string;
  value: number | undefined;
};

export const RecyclingRateTooltip: React.FC<Props> = ({
  active,
  payload,
  label,
}) => {
  const { t } = useTrans();
  const { target, showTarget, showCompareYear, showPortfolioAverage } =
    useContext(RecyclingRateTrendContext);

  const point: RecyclingRatePoint | undefined = useMemo(
    () => payload?.[0]?.payload,
    [payload],
  );

  const rows = useMemo(
    () =>
      [
        {
          key: "rate",
          name: t("homePage:trendTooltipRate"),
          color: "#5B8C3E",
          value: point?.rate,
        },
        showCompareYear &&
          point?.prevYearRate != null && {
            key: "prevYearRate",
            name: t("homePage:trendTooltipYearBefore"),
            color: "#C5BEBE",
            value: point.prevYearRate,
          },
        showPortfolioAverage &&
          point?.portfolioAverage != null && {
            key: "portfolioAverage",
            name: t("homePage:trendTooltipPortfolioAverage"),
            color: "#B73A23",
            value: point.portfolioAverage,
          },
        showTarget && {
          key: "target",
          name: t("homePage:trendTooltipTarget"),
          color: "#467432",
          value: target,
        },
      ].filter(Boolean) as TooltipData[],
    [point, showCompareYear, showPortfolioAverage, showTarget, t, target],
  );

  if (!active || !payload?.length || !point) {
    return null;
  }

  return (
    <div className="rounded-lg bg-black px-[9.9px] py-1.5 text-[12px] text-white shadow-lg">
      <div className="mb-[4.95px] font-semibold">{label}</div>
      {rows.map((r) => (
        <div
          key={r.key}
          className="mt-[1.65px] flex items-center justify-between gap-[11.55px]"
        >
          <span className="flex items-center gap-[4.95px]">
            <span
              className="inline-block h-1.5 w-1.5 flex-none rounded-sm border border-white/30"
              style={{ background: r.color }}
            />
            {r.name}
          </span>
          {r.value != null && (
            <span className="pl-1.5 font-semibold whitespace-nowrap">
              {r.value.toFixed(1)}%
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
